/**
 * PatientTimelineTab — Unified chronological timeline for a patient
 *
 * Aggregates: lab orders, intake submissions, prescriptions, and notes
 * into a single scrollable timeline. Each event is clickable and shows
 * a summary with the option to expand for detail.
 *
 * Part of Phase 5 — Integration Polish.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FlaskConical,
  ClipboardList,
  Pill,
  Sparkles,
  FileText,
  Calendar,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react';
import { api } from '@/services/api';
import { formatDOB } from '@/lib/dateUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TimelineEvent {
  id: string;
  type: 'lab_order' | 'intake' | 'prescription' | 'recommendation' | 'note';
  title: string;
  subtitle?: string;
  date: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

interface PatientTimelineTabProps {
  patientId: string;
}

// ─── Event Type Config ────────────────────────────────────────────────────────

const EVENT_CONFIG: Record<TimelineEvent['type'], {
  icon: typeof FlaskConical;
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
}> = {
  lab_order: {
    icon: FlaskConical,
    color: 'text-brand-teal',
    bgColor: 'bg-brand-teal/10',
    borderColor: 'border-brand-teal/20',
    label: 'Lab Order',
  },
  intake: {
    icon: ClipboardList,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    label: 'Intake',
  },
  prescription: {
    icon: Pill,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    label: 'Prescription',
  },
  recommendation: {
    icon: Sparkles,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    label: 'AI Recommendation',
  },
  note: {
    icon: FileText,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    label: 'Clinical Note',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const PatientTimelineTab: React.FC<PatientTimelineTabProps> = ({ patientId }) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<TimelineEvent['type'] | 'all'>('all');

  useEffect(() => {
    if (!patientId) return;

    const fetchTimeline = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch all data sources in parallel
        const [labsRes, prescriptionsRes, intakesRes] = await Promise.allSettled([
          api.get<{ data: { orders: Array<Record<string, unknown>> } }>(`/lab-orders?patient_id=${patientId}&limit=100`),
          api.get<{ data: { prescriptions: Array<Record<string, unknown>> } }>(`/prescriptions?patient_id=${patientId}&limit=100`),
          api.get<{ data: { pending: Array<Record<string, unknown>>; completed: Array<Record<string, unknown>>; reviewed: Array<Record<string, unknown>> } }>(`/intakes/patient/${patientId}/status`),
        ]);

        const timelineEvents: TimelineEvent[] = [];

        // Lab orders
        if (labsRes.status === 'fulfilled' && labsRes.value?.data?.orders) {
          for (const order of labsRes.value.data.orders) {
            timelineEvents.push({
              id: `lab-${order.id}`,
              type: 'lab_order',
              title: String(order.panel_name || 'Lab Panel'),
              subtitle: order.provider_name ? `Provider: ${order.provider_name}` : undefined,
              date: String(order.created_at || order.ordered_at || ''),
              status: String(order.status || ''),
              metadata: { resultCount: Array.isArray(order.results) ? order.results.length : 0 },
            });
          }
        }

        // Prescriptions
        if (prescriptionsRes.status === 'fulfilled' && prescriptionsRes.value?.data?.prescriptions) {
          for (const rx of prescriptionsRes.value.data.prescriptions) {
            const meds = Array.isArray(rx.medications) ? rx.medications : [];
            const medNames = meds.map((m: Record<string, unknown>) => String(m.name || m.medication_name || '')).filter(Boolean);
            timelineEvents.push({
              id: `rx-${rx.id}`,
              type: 'prescription',
              title: medNames.length > 0 ? medNames.join(', ') : 'Prescription',
              subtitle: rx.provider_name ? `Prescribed by ${rx.provider_name}` : undefined,
              date: String(rx.created_at || rx.prescribed_at || ''),
              status: String(rx.status || ''),
              metadata: { medicationCount: meds.length },
            });
          }
        }

        // Intakes
        if (intakesRes.status === 'fulfilled' && intakesRes.value?.data) {
          const intakeData = intakesRes.value.data;
          const allIntakes = [
            ...(intakeData.pending || []).map((i: Record<string, unknown>) => ({ ...i, _intakeStatus: 'pending' })),
            ...(intakeData.completed || []).map((i: Record<string, unknown>) => ({ ...i, _intakeStatus: 'completed' })),
            ...(intakeData.reviewed || []).map((i: Record<string, unknown>) => ({ ...i, _intakeStatus: 'reviewed' })),
          ];

          for (const intake of allIntakes) {
            timelineEvents.push({
              id: `intake-${intake.id || intake.response_id}`,
              type: 'intake',
              title: String(intake.intake_type || intake.therapy_category || 'Intake Form'),
              subtitle: intake._intakeStatus ? `Status: ${intake._intakeStatus}` : undefined,
              date: String(intake.created_at || intake.submitted_at || ''),
              status: String(intake._intakeStatus || ''),
            });
          }
        }

        // Sort by date descending (newest first)
        timelineEvents.sort((a, b) => {
          const dateA = new Date(a.date).getTime() || 0;
          const dateB = new Date(b.date).getTime() || 0;
          return dateB - dateA;
        });

        setEvents(timelineEvents);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load timeline');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeline();
  }, [patientId]);

  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return events;
    return events.filter(e => e.type === filterType);
  }, [events, filterType]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Count by type for filter badges
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of events) {
      counts[e.type] = (counts[e.type] || 0) + 1;
    }
    return counts;
  }, [events]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '--';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="py-8 text-center">
          <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-12 text-center">
          <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No timeline events yet</p>
          <p className="text-sm text-slate-400 mt-1">Lab orders, intakes, and prescriptions will appear here</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filterType === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterType('all')}
          className="h-7 text-xs"
        >
          All ({events.length})
        </Button>
        {(Object.keys(EVENT_CONFIG) as TimelineEvent['type'][]).map(type => {
          const count = typeCounts[type] || 0;
          if (count === 0) return null;
          const config = EVENT_CONFIG[type];
          return (
            <Button
              key={type}
              variant={filterType === type ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType(type)}
              className="h-7 text-xs"
            >
              <config.icon className="h-3 w-3 mr-1" />
              {config.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-200" />

        <div className="space-y-1">
          {filteredEvents.map((event, index) => {
            const config = EVENT_CONFIG[event.type];
            const Icon = config.icon;
            const isExpanded = expandedIds.has(event.id);

            // Check if this is a new date group
            const currentDate = formatDate(event.date);
            const prevDate = index > 0 ? formatDate(filteredEvents[index - 1].date) : null;
            const showDateHeader = currentDate !== prevDate;

            return (
              <React.Fragment key={event.id}>
                {/* Date Header */}
                {showDateHeader && (
                  <div className="flex items-center gap-3 py-2">
                    <div className="w-10 flex justify-center">
                      <div className="w-2 h-2 rounded-full bg-slate-300" />
                    </div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {currentDate}
                    </span>
                  </div>
                )}

                {/* Event Card */}
                <div
                  className="flex gap-3 group cursor-pointer"
                  onClick={() => toggleExpand(event.id)}
                >
                  {/* Icon */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center border ${config.borderColor}`}>
                      <Icon className={`h-4 w-4 ${config.color}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-4">
                    <div className="bg-white border border-slate-200 rounded-lg p-3 group-hover:border-slate-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900 capitalize truncate">
                              {event.title}
                            </span>
                            <Badge variant="outline" className="text-[10px] flex-shrink-0">
                              {config.label}
                            </Badge>
                          </div>
                          {event.subtitle && (
                            <p className="text-xs text-slate-500 mt-0.5">{event.subtitle}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <span className="text-[11px] text-slate-400">{formatTime(event.date)}</span>
                          {event.status && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                event.status === 'completed' || event.status === 'reviewed' || event.status === 'active'
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : event.status === 'pending'
                                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                                  : 'border-slate-200 text-slate-500'
                              }`}
                            >
                              {event.status}
                            </Badge>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Detail */}
                      {isExpanded && event.metadata && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="text-xs text-slate-600 space-y-1">
                            {event.type === 'lab_order' && event.metadata.resultCount !== undefined && (
                              <p>Results: {Number(event.metadata.resultCount)} biomarkers</p>
                            )}
                            {event.type === 'prescription' && event.metadata.medicationCount !== undefined && (
                              <p>Medications: {Number(event.metadata.medicationCount)}</p>
                            )}
                            <p className="text-slate-400">ID: {event.id.split('-').slice(1).join('-')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PatientTimelineTab;
